import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, MapPin, Sparkles, TrendingUp, Clock, CheckCircle, UserCheck, Trash2, RefreshCw, Settings, Save, MessageCircle, Mail, Calendar, Search as SearchIcon } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('leads');
  const [settings, setSettings] = useState({
    whatsapp_number: '',
    admin_email: '',
    notification_email: '',
    calendly_url: '',
    seo_title: '',
    seo_description: '',
    seo_keywords: ''
  });
  const [savingSettings, setSavingSettings] = useState(false);
  const navigate = useNavigate();

  const getAuthHeader = useCallback(() => {
    const stored = localStorage.getItem('velmont_admin');
    if (stored) {
      const { username, password } = JSON.parse(stored);
      return { Authorization: `Basic ${btoa(`${username}:${password}`)}` };
    }
    return {};
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const authHeader = { Authorization: `Basic ${btoa(`${credentials.username}:${credentials.password}`)}` };
      await axios.get(`${API}/contacts`, { headers: authHeader });
      localStorage.setItem('velmont_admin', JSON.stringify(credentials));
      setIsAuthenticated(true);
      toast.success('Welcome back');
    } catch (error) {
      toast.error('Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const headers = getAuthHeader();
      const [leadsRes, statsRes, settingsRes] = await Promise.all([
        axios.get(`${API}/contacts`, { headers }),
        axios.get(`${API}/admin/stats`, { headers }),
        axios.get(`${API}/settings`)
      ]);
      setLeads(leadsRes.data);
      setStats(statsRes.data);
      setSettings(settingsRes.data);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem('velmont_admin');
        setIsAuthenticated(false);
      }
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/contacts/${id}/status`, { status }, { headers: getAuthHeader() });
      toast.success('Status updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const deleteLead = async (id) => {
    if (!window.confirm('Delete this lead?')) return;
    try {
      await axios.delete(`${API}/contacts/${id}`, { headers: getAuthHeader() });
      toast.success('Lead deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await axios.put(`${API}/settings`, settings, { headers: getAuthHeader() });
      toast.success('Settings saved successfully');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const exportCSV = () => {
    const headers = ['Name', 'Email', 'Phone', 'City', 'Experience', 'Status', 'Date', 'Message'];
    const rows = leads.map(l => [
      l.name,
      l.email,
      l.phone || '',
      l.city || '',
      l.experience_type || '',
      l.status,
      new Date(l.created_at).toLocaleDateString(),
      l.message || ''
    ]);
    
    const csv = [headers, ...rows].map(r => r.map(c => `"${c}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `velmont-leads-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  useEffect(() => {
    const stored = localStorage.getItem('velmont_admin');
    if (stored) {
      setIsAuthenticated(true);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchData();
    }
  }, [isAuthenticated, fetchData]);

  const filteredLeads = filter === 'all' ? leads : leads.filter(l => l.status === filter);

  const statusColors = {
    new: 'bg-blue-500/20 text-blue-400',
    contacted: 'bg-yellow-500/20 text-yellow-400',
    qualified: 'bg-purple-500/20 text-purple-400',
    converted: 'bg-green-500/20 text-green-400'
  };

  const cityNames = {
    'sao-paulo': 'São Paulo',
    'rio-de-janeiro': 'Rio de Janeiro',
    'both': 'Both Cities'
  };

  const experienceNames = {
    'nightlife': 'Nightlife',
    'private': 'Private',
    'events': 'Events',
    'custom': 'Custom'
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center px-6">
        <div className="w-full max-w-md">
          <div className="text-center mb-10">
            <h1 className="font-playfair text-3xl text-[#F5F5F0] mb-3">Admin Access</h1>
            <p className="text-[#52525B] text-sm font-manrope">Velmont Society Dashboard</p>
          </div>
          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <input
                type="text"
                placeholder="Username"
                value={credentials.username}
                onChange={(e) => setCredentials({ ...credentials, username: e.target.value })}
                className="w-full bg-obsidian-paper border border-white/10 px-4 py-3 text-[#F5F5F0] placeholder:text-[#52525B] focus:outline-none focus:border-gold transition-colors font-manrope"
                data-testid="admin-username"
              />
            </div>
            <div>
              <input
                type="password"
                placeholder="Password"
                value={credentials.password}
                onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                className="w-full bg-obsidian-paper border border-white/10 px-4 py-3 text-[#F5F5F0] placeholder:text-[#52525B] focus:outline-none focus:border-gold transition-colors font-manrope"
                data-testid="admin-password"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gold text-black py-3 text-xs uppercase tracking-widest font-bold hover:bg-gold-light transition-all disabled:opacity-50"
              data-testid="admin-login-btn"
            >
              {loading ? 'Accessing...' : 'Access Dashboard'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="min-h-screen pt-24 pb-16 px-6 md:px-12">
      <div className="max-w-[1400px] mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-10">
          <div>
            <h1 className="font-playfair text-3xl text-[#F5F5F0] mb-2">Admin Dashboard</h1>
            <p className="text-[#52525B] text-sm font-manrope">Manage leads and settings</p>
          </div>
          <div className="flex gap-3 mt-4 md:mt-0">
            <button
              onClick={fetchData}
              className="flex items-center gap-2 px-4 py-2 border border-white/10 text-[#A1A1AA] hover:border-gold hover:text-gold transition-colors text-sm"
              data-testid="refresh-btn"
            >
              <RefreshCw size={14} />
              Refresh
            </button>
            <button
              onClick={() => {
                localStorage.removeItem('velmont_admin');
                setIsAuthenticated(false);
              }}
              className="px-4 py-2 text-[#52525B] hover:text-red-400 transition-colors text-sm"
            >
              Logout
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-4 mb-8 border-b border-white/5">
          <button
            onClick={() => setActiveTab('leads')}
            className={`pb-4 text-sm uppercase tracking-wide transition-colors ${
              activeTab === 'leads'
                ? 'text-gold border-b-2 border-gold'
                : 'text-[#52525B] hover:text-[#A1A1AA]'
            }`}
          >
            <Users size={16} className="inline mr-2" />
            Leads
          </button>
          <button
            onClick={() => setActiveTab('settings')}
            data-testid="settings-tab"
            className={`pb-4 text-sm uppercase tracking-wide transition-colors ${
              activeTab === 'settings'
                ? 'text-gold border-b-2 border-gold'
                : 'text-[#52525B] hover:text-[#A1A1AA]'
            }`}
          >
            <Settings size={16} className="inline mr-2" />
            Settings
          </button>
        </div>

        {activeTab === 'leads' && (
          <>
            {/* Stats Grid */}
            {stats && (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-10">
                <div className="bg-obsidian-paper border border-white/5 p-6">
                  <Users className="text-gold mb-3" size={20} />
                  <p className="text-2xl font-playfair text-[#F5F5F0]">{stats.total_leads}</p>
                  <p className="text-xs text-[#52525B] uppercase tracking-wide mt-1">Total Leads</p>
                </div>
                <div className="bg-obsidian-paper border border-white/5 p-6">
                  <Clock className="text-blue-400 mb-3" size={20} />
                  <p className="text-2xl font-playfair text-[#F5F5F0]">{stats.new_leads}</p>
                  <p className="text-xs text-[#52525B] uppercase tracking-wide mt-1">New</p>
                </div>
                <div className="bg-obsidian-paper border border-white/5 p-6">
                  <TrendingUp className="text-yellow-400 mb-3" size={20} />
                  <p className="text-2xl font-playfair text-[#F5F5F0]">{stats.contacted_leads}</p>
                  <p className="text-xs text-[#52525B] uppercase tracking-wide mt-1">Contacted</p>
                </div>
                <div className="bg-obsidian-paper border border-white/5 p-6">
                  <UserCheck className="text-purple-400 mb-3" size={20} />
                  <p className="text-2xl font-playfair text-[#F5F5F0]">{stats.qualified_leads}</p>
                  <p className="text-xs text-[#52525B] uppercase tracking-wide mt-1">Qualified</p>
                </div>
                <div className="bg-obsidian-paper border border-white/5 p-6">
                  <CheckCircle className="text-green-400 mb-3" size={20} />
                  <p className="text-2xl font-playfair text-[#F5F5F0]">{stats.converted_leads}</p>
                  <p className="text-xs text-[#52525B] uppercase tracking-wide mt-1">Converted</p>
                </div>
              </div>
            )}

            {/* Filters & Export */}
            <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
              <div className="flex gap-2 flex-wrap">
                {['all', 'new', 'contacted', 'qualified', 'converted'].map((f) => (
                  <button
                    key={f}
                    onClick={() => setFilter(f)}
                    className={`px-4 py-2 text-xs uppercase tracking-wide transition-colors ${
                      filter === f
                        ? 'bg-gold text-black'
                        : 'border border-white/10 text-[#A1A1AA] hover:border-gold'
                    }`}
                  >
                    {f}
                  </button>
                ))}
              </div>
              <button
                onClick={exportCSV}
                className="flex items-center gap-2 px-4 py-2 bg-gold text-black text-sm font-bold hover:bg-gold-light transition-colors"
                data-testid="export-btn"
              >
                Export CSV
              </button>
            </div>

            {/* Leads Table */}
            <div className="bg-obsidian-paper border border-white/5 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/5">
                      <th className="text-left px-6 py-4 text-xs uppercase tracking-wide text-[#52525B] font-manrope">Name</th>
                      <th className="text-left px-6 py-4 text-xs uppercase tracking-wide text-[#52525B] font-manrope">Contact</th>
                      <th className="text-left px-6 py-4 text-xs uppercase tracking-wide text-[#52525B] font-manrope">Interest</th>
                      <th className="text-left px-6 py-4 text-xs uppercase tracking-wide text-[#52525B] font-manrope">Status</th>
                      <th className="text-left px-6 py-4 text-xs uppercase tracking-wide text-[#52525B] font-manrope">Date</th>
                      <th className="text-left px-6 py-4 text-xs uppercase tracking-wide text-[#52525B] font-manrope">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredLeads.map((lead) => (
                      <tr key={lead.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                        <td className="px-6 py-4">
                          <p className="text-[#F5F5F0] font-manrope">{lead.name}</p>
                          {lead.message && (
                            <p className="text-[#52525B] text-xs mt-1 max-w-xs truncate">{lead.message}</p>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <p className="text-[#A1A1AA] text-sm font-manrope">{lead.email}</p>
                          {lead.phone && <p className="text-[#52525B] text-xs mt-1">{lead.phone}</p>}
                        </td>
                        <td className="px-6 py-4">
                          <p className="text-[#A1A1AA] text-sm">{cityNames[lead.city] || '-'}</p>
                          <p className="text-[#52525B] text-xs mt-1">{experienceNames[lead.experience_type] || '-'}</p>
                        </td>
                        <td className="px-6 py-4">
                          <select
                            value={lead.status}
                            onChange={(e) => updateStatus(lead.id, e.target.value)}
                            className={`px-3 py-1 text-xs uppercase tracking-wide bg-transparent border-0 cursor-pointer ${statusColors[lead.status]}`}
                          >
                            <option value="new" className="bg-obsidian">New</option>
                            <option value="contacted" className="bg-obsidian">Contacted</option>
                            <option value="qualified" className="bg-obsidian">Qualified</option>
                            <option value="converted" className="bg-obsidian">Converted</option>
                          </select>
                        </td>
                        <td className="px-6 py-4 text-[#52525B] text-sm">
                          {new Date(lead.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4">
                          <button
                            onClick={() => deleteLead(lead.id)}
                            className="text-[#52525B] hover:text-red-400 transition-colors"
                          >
                            <Trash2 size={16} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {filteredLeads.length === 0 && (
                <div className="text-center py-12 text-[#52525B]">
                  No leads found
                </div>
              )}
            </div>
          </>
        )}

        {activeTab === 'settings' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Contact Settings */}
            <div className="bg-obsidian-paper border border-white/5 p-8">
              <h3 className="font-playfair text-xl text-[#F5F5F0] mb-6 flex items-center gap-3">
                <MessageCircle className="text-gold" size={20} />
                Contact Settings
              </h3>
              
              <div className="space-y-6">
                <div>
                  <label className="text-xs uppercase tracking-widest text-[#52525B] mb-3 block font-manrope">
                    WhatsApp Number
                  </label>
                  <input
                    type="text"
                    value={settings.whatsapp_number || ''}
                    onChange={(e) => setSettings({ ...settings, whatsapp_number: e.target.value })}
                    placeholder="+1234567890"
                    data-testid="settings-whatsapp"
                    className="w-full bg-transparent border border-white/10 focus:border-gold px-4 py-3 text-[#F5F5F0] placeholder:text-[#3F3F46] focus:outline-none transition-colors font-manrope"
                  />
                  <p className="text-[#52525B] text-xs mt-2">Include country code (e.g., +1 for US)</p>
                </div>

                <div>
                  <label className="text-xs uppercase tracking-widest text-[#52525B] mb-3 block font-manrope">
                    Admin Email
                  </label>
                  <input
                    type="email"
                    value={settings.admin_email || ''}
                    onChange={(e) => setSettings({ ...settings, admin_email: e.target.value })}
                    placeholder="admin@velmont.com"
                    data-testid="settings-admin-email"
                    className="w-full bg-transparent border border-white/10 focus:border-gold px-4 py-3 text-[#F5F5F0] placeholder:text-[#3F3F46] focus:outline-none transition-colors font-manrope"
                  />
                </div>

                <div>
                  <label className="text-xs uppercase tracking-widest text-[#52525B] mb-3 block font-manrope">
                    Lead Notification Email
                  </label>
                  <input
                    type="email"
                    value={settings.notification_email || ''}
                    onChange={(e) => setSettings({ ...settings, notification_email: e.target.value })}
                    placeholder="leads@velmont.com"
                    data-testid="settings-notification-email"
                    className="w-full bg-transparent border border-white/10 focus:border-gold px-4 py-3 text-[#F5F5F0] placeholder:text-[#3F3F46] focus:outline-none transition-colors font-manrope"
                  />
                  <p className="text-[#52525B] text-xs mt-2">New lead alerts will be sent here</p>
                </div>

                <div>
                  <label className="text-xs uppercase tracking-widest text-[#52525B] mb-3 block font-manrope">
                    Calendar Booking URL
                  </label>
                  <input
                    type="url"
                    value={settings.calendly_url || ''}
                    onChange={(e) => setSettings({ ...settings, calendly_url: e.target.value })}
                    placeholder="https://cal.com/your-link"
                    data-testid="settings-calendar"
                    className="w-full bg-transparent border border-white/10 focus:border-gold px-4 py-3 text-[#F5F5F0] placeholder:text-[#3F3F46] focus:outline-none transition-colors font-manrope"
                  />
                </div>
              </div>
            </div>

            {/* SEO Settings */}
            <div className="bg-obsidian-paper border border-white/5 p-8">
              <h3 className="font-playfair text-xl text-[#F5F5F0] mb-6 flex items-center gap-3">
                <SearchIcon className="text-gold" size={20} />
                SEO Settings
              </h3>
              
              <div className="space-y-6">
                <div>
                  <label className="text-xs uppercase tracking-widest text-[#52525B] mb-3 block font-manrope">
                    Page Title
                  </label>
                  <input
                    type="text"
                    value={settings.seo_title || ''}
                    onChange={(e) => setSettings({ ...settings, seo_title: e.target.value })}
                    placeholder="Velmont Society | Private Luxury Concierge"
                    data-testid="settings-seo-title"
                    className="w-full bg-transparent border border-white/10 focus:border-gold px-4 py-3 text-[#F5F5F0] placeholder:text-[#3F3F46] focus:outline-none transition-colors font-manrope"
                  />
                </div>

                <div>
                  <label className="text-xs uppercase tracking-widest text-[#52525B] mb-3 block font-manrope">
                    Meta Description
                  </label>
                  <textarea
                    value={settings.seo_description || ''}
                    onChange={(e) => setSettings({ ...settings, seo_description: e.target.value })}
                    placeholder="Private luxury concierge creating bespoke VIP experiences in Brazil..."
                    rows={3}
                    data-testid="settings-seo-description"
                    className="w-full bg-transparent border border-white/10 focus:border-gold px-4 py-3 text-[#F5F5F0] placeholder:text-[#3F3F46] focus:outline-none transition-colors font-manrope resize-none"
                  />
                  <p className="text-[#52525B] text-xs mt-2">Recommended: 150-160 characters</p>
                </div>

                <div>
                  <label className="text-xs uppercase tracking-widest text-[#52525B] mb-3 block font-manrope">
                    Keywords
                  </label>
                  <input
                    type="text"
                    value={settings.seo_keywords || ''}
                    onChange={(e) => setSettings({ ...settings, seo_keywords: e.target.value })}
                    placeholder="luxury concierge, Brazil, VIP experiences..."
                    data-testid="settings-seo-keywords"
                    className="w-full bg-transparent border border-white/10 focus:border-gold px-4 py-3 text-[#F5F5F0] placeholder:text-[#3F3F46] focus:outline-none transition-colors font-manrope"
                  />
                  <p className="text-[#52525B] text-xs mt-2">Comma-separated keywords</p>
                </div>
              </div>
            </div>

            {/* Save Button */}
            <div className="lg:col-span-2">
              <button
                onClick={saveSettings}
                disabled={savingSettings}
                data-testid="save-settings-btn"
                className="flex items-center gap-3 bg-gold text-black px-8 py-4 text-xs uppercase tracking-widest font-bold hover:bg-gold-light transition-all disabled:opacity-50"
              >
                <Save size={16} />
                {savingSettings ? 'Saving...' : 'Save All Settings'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
